import MediaApi from '@/misc/apis/MediaApi'
import Vue from 'vue'

const media = {
  methods: {
    getUrlOfThumbnailS(medium) {
      for (const thumbnail of medium.thumbnails || []) {
        if (thumbnail.type === 'S') {
          return thumbnail.url
        }
      }

      return medium.url
    },
    updateUploadedFileName(ctx, fileName) {
      if (this.$refs['file-input']?.$el?.children?.length && this.$refs['file-input'].$el.children[1].children?.length && this.$refs['file-input'].$el.children[1].children[0].children.length) {
        ctx.$refs['file-input'].$el.children[1].children[0].children[0].innerHTML = fileName
      }
    },
    editFileName(name) {
      return name
        .replace(/\u00df/g, 'ss')
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00c4/g, 'Ae')
        .replace(/\u00d6/g, 'Oe')
        .replace(/\u00dc/g, 'Ue')
    },
    getFileHeader(blob: Blob) {
      return new Promise(resolve => {
        const headerBytes = blob.slice(0, 4) // Read the first 4 bytes of the file
        const fileReader = new FileReader()
        fileReader.onloadend = (e: ProgressEvent<FileReader>) => {
          const arr = new Uint8Array(e?.target?.result as ArrayBufferLike).subarray(0, 4)
          let header = ''
          for (let i = 0; i < arr.length; i++) {
            header += arr[i].toString(16)
          }
          resolve(header)
        }
        fileReader.readAsArrayBuffer(headerBytes)
      })
    },
    mergeDownloadAllDocuments(idsAndNames, nameOfMergedFile) {
      Vue.toasted.show(this.$tc('message.successMessages.serialLetterCreated'), { type: 'success' })
      MediaApi.mergeAllDocuments(idsAndNames, nameOfMergedFile).then(async response => {
        const file = response.media
        const binary = window.atob(file)
        const len = binary.length
        const buffer = new ArrayBuffer(len)
        const view = new Uint8Array(buffer)
        for (let i = 0; i < len; i++) {
          view[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([view])
        // check if file type is pdf
        const pdfBytePattern = '25504446'
        const fileHeader = await this.getFileHeader(blob)
        const isPdf = fileHeader === pdfBytePattern

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = nameOfMergedFile + (isPdf ? '.pdf' : '.zip')
        link.click()
      })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.applicationForm.creditTrust.certificateOrderError'), { type: 'error' })
        })
    },
    async getFilesFromEvent(event) {
      const listAcceptedFiles = []
      let fileFromZip = false
      if (event && event.dataTransfer && event.dataTransfer.getData('itemId')) {
        try {
          const response = await MediaApi.getMediaByID(event.dataTransfer.getData('itemId'))
          const media = response.media
          media.name = media.filename
          return [media]
        } catch (error) {
          console.log(error)
          Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
        }
      }
      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0]
        if (file.size === 0) {
          fileFromZip = true
        } else {
          for (let index = 0; index < event.dataTransfer.files.length; index++) {
            const element = event.dataTransfer.files[index]
            listAcceptedFiles.push(element)
          }
          return listAcceptedFiles
        }
      }
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0]
        if (file.size === 0) {
          fileFromZip = true
        } else {
          for (let index = 0; index < event.target.files.length; index++) {
            const element = event.target.files[index]
            listAcceptedFiles.push(element)
          }
          return listAcceptedFiles
        }
      }
      if (fileFromZip) {
        Vue.toasted.show(this.$tc('message.uploadingErrors.zipFileNotAccepted'), { type: 'error' })
      }
    },

    // Helped by ChatGPT
    async loadImageSource(file) {
      if (typeof createImageBitmap === 'function') {
        try {
          const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
          return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            cleanup: () => bitmap.close && bitmap.close()
          }
        } catch (error) {
          console.log(error)
        }
      }

      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
          URL.revokeObjectURL(url)
          resolve({
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight
          })
        }
        image.onerror = (error) => {
          URL.revokeObjectURL(url)
          reject(error)
        }
        image.src = url
      })
    },
    async normalizeAndResizeImage(file, maxDimension) {
      if (!maxDimension || !file?.type?.startsWith('image/')) {
        return file
      }

      try {
        const { source, width, height, cleanup } = await this.loadImageSource(file)
        const maxSide = Math.max(width, height)
        if (!maxSide || maxSide <= maxDimension) {
          cleanup && cleanup()
          return file
        }

        const scale = maxDimension / maxSide
        const targetWidth = Math.round(width * scale)
        const targetHeight = Math.round(height * scale)
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          cleanup && cleanup()
          return file
        }

        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(source, 0, 0, targetWidth, targetHeight)
        cleanup && cleanup()

        const outputType = file.type === 'image/jpg' ? 'image/jpeg' : file.type
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, outputType || 'image/jpeg', 0.92))
        if (!blob) {
          return file
        }

        return new File([blob], file.name, { type: blob.type || file.type })
      } catch (error) {
        console.log(error)
        return file
      }
    },
    async uploadMedia(files, ownerId = '', filePublic = 'false', maxDimension = null, onUploadProgress = null) {
      const promises = files
        .filter(element => element !== null)
        .map(async element => {
          const normalizedFile = await this.normalizeAndResizeImage(element, maxDimension)
          const formData = new FormData()
          const originalName = normalizedFile?.name || element?.name || 'upload'
          const fileName = this.editFileName(originalName)
          formData.append('file', normalizedFile, fileName)
          formData.append('filename', originalName)
          formData.append('public', filePublic)
          if (ownerId) {
            formData.append('accountId', ownerId.toString())
          }
          if (maxDimension) {
            formData.append('maxDimension', maxDimension)
          }
          return MediaApi.uploadMedia(formData, onUploadProgress)
        })

      return Promise.all(promises)
        .then(response => {
          return response
        })
        .catch(e => {
          console.log(e)
          if (e.response && e.response.data && e.response.data.message === 'Not supported MIME type') {
            Vue.toasted.show(this.$t('message.savingErrors.fileNotSupported'), { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
          }
          return null
        })
    }
  }
}

export { media }
